import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { AudienceLevel, Industry, SavedPathway } from '../../../types/pathway';
import { PathwayButton } from '../PathwayButton';
import { PathwayLibraryFilters } from '../PathwayLibraryFilters';
import { PathwayPagination } from '../PathwayPagination';
import { SavedPathwayRow } from './SavedPathwayRow';
import { matchesPathwayLibraryFilters, pathwayTopicOptions } from '../../../utils/pathwayLibrary';
const PAGE_SIZE = 5;
const SORT_OPTIONS = [{
  value: 'newest',
  label: 'Sort: Newest'
}, {
  value: 'name-asc',
  label: 'Sort: A–Z'
}, {
  value: 'name-desc',
  label: 'Sort: Z–A'
}];
interface SavedPathwaysViewProps {
  pathways: SavedPathway[];
  onOpen: (pathway: SavedPathway) => void;
  onDuplicate: (pathway: SavedPathway) => void;
  onExport: (pathways: SavedPathway[]) => void;
}
export function SavedPathwaysView({
  pathways,
  onOpen,
  onDuplicate,
  onExport
}: SavedPathwaysViewProps) {
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState<Industry | 'all'>('all');
  const [role, setRole] = useState<AudienceLevel | 'all'>('all');
  const [topic, setTopic] = useState('all');
  const [sort, setSort] = useState('newest');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const topics = useMemo(() => pathwayTopicOptions(pathways.map(pathway => pathway.moduleIds)), [pathways]);
  const visible = useMemo(() => pathways.filter(pathway => matchesPathwayLibraryFilters(pathway.moduleIds, pathway.audience, `${pathway.name} ${pathway.organization}`, {
    query,
    industry,
    role,
    topic
  })).slice().sort((a, b) => {
    if (sort === 'name-asc') return a.name.localeCompare(b.name);
    if (sort === 'name-desc') return b.name.localeCompare(a.name);
    return b.updatedAt.localeCompare(a.updatedAt);
  }), [pathways, query, industry, role, topic, sort]);
  const isFiltered = query.trim().length > 0 || industry !== 'all' || role !== 'all' || topic !== 'all' || sort !== 'newest';
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const selectedPathways = pathways.filter(pathway => selectedIds.has(pathway.id));
  const allPageSelected = pageItems.length > 0 && pageItems.every(pathway => selectedIds.has(pathway.id));
  const update = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };
  const resetSearch = () => {
    setQuery('');
    setIndustry('all');
    setRole('all');
    setTopic('all');
    setSort('newest');
    setPage(1);
  };
  const toggleSelection = (id: string) => {
    setSelectedIds(current => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const togglePageSelection = () => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (allPageSelected) {
        pageItems.forEach(pathway => next.delete(pathway.id));
      } else {
        pageItems.forEach(pathway => next.add(pathway.id));
      }
      return next;
    });
  };
  return <section aria-labelledby="saved-pathways-heading" data-id="element-2027" data-mp="ah6pk9no1x31">
      <div className="mb-5" data-id="element-2028" data-mp="afn3g2fbwrtc">
        <h2 id="saved-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-2029" data-mp="f8oq4wls1vnh">
          Your pathways{pathways.length > 0 ? ` (${pathways.length})` : ''}
        </h2>
        <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-2030" data-mp="k9cd976vxscf">
          Save pathways here, then select one or more to create a combined
          shareable package.
        </p>
      </div>

      {pathways.length > 0 && <PathwayLibraryFilters searchLabel="Search your pathways" searchPlaceholder="Search by name or company" query={query} industry={industry} role={role} topic={topic} topics={topics} sort={sort} sortOptions={SORT_OPTIONS} isActive={isFiltered} onQueryChange={update(setQuery)} onIndustryChange={update(setIndustry)} onRoleChange={update(setRole)} onTopicChange={update(setTopic)} onSortChange={update(setSort)} onReset={resetSearch} data-id="element-2031" data-mp="fl8vi7j9sdzu" />}

      {pathways.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-2032" data-mp="6mdmyosr6rtd">
          <h3 className="text-base font-bold text-gray-900" data-id="element-2033" data-mp="1keitcp6vqvd">No pathways yet</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-600" data-id="element-2034" data-mp="1hyo7y9199jn9">
            Build one from a conversation, a skill module, or a reusable pathway.
          </p>
        </div> : visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-2035" data-mp="2uwy8bce886bm">
          <p className="text-sm text-gray-600" data-id="element-2036" data-mp="2zyvx0m5ebtby">No pathways match those filters.</p>
          <button type="button" onClick={resetSearch} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-2037" data-mp="2lz4mive38v2p">
            Reset search
          </button>
        </div> : <div data-id="element-2038" data-mp="3inu5kp1gbtq9">
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-2039" data-mp="3iobybbownptf">
            <div className="overflow-x-auto" data-id="element-2040" data-mp="34xgz6r47kn31">
              <table className="w-full table-fixed sm:min-w-[820px]" data-id="element-2041" data-mp="34ul02zgmjsd0">
                <caption className="sr-only" data-id="element-2042" data-mp="39uh42ypbnjsk">
                  Saved pathways — select rows to create a combined export, or
                  expand a row to see its modules
                </caption>
                <colgroup data-id="element-2043" data-mp="67gr54eq3am0">
                  <col className="w-[52px]" data-id="element-2044" data-mp="68rux4864id4" />
                  <col data-id="element-2045" data-mp="g2xgg7ztlye6" />
                  <col className="w-[180px]" data-id="element-2046" data-mp="g1cnunx2gpiy" />
                  <col className="w-[135px]" data-id="element-2047" data-mp="b3kt7m51epb2" />
                  <col className="w-[190px]" data-id="element-2048" data-mp="b4vyxri1m74t" />
                </colgroup>
                <thead data-id="element-2049" data-mp="63yf7ahdx517">
                  <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2050" data-mp="636k00rp99bg">
                    <th className="px-3 py-3 text-center sm:px-4" data-id="element-2051" data-mp="15woq4hmq3fp">
                      <input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} aria-label="Select all pathways on this page" className="h-4 w-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-2052" data-mp="17r7pd9cblh5" />
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-4" data-id="element-2053" data-mp="3eigi6xsgxudy">
                      Pathway
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-4" data-id="element-2054" data-mp="35k4up4mwfcpq">
                      Audience
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-4" data-id="element-2055" data-mp="1p24ios0xmj4h">
                      Modules
                    </th>
                    <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-4" data-id="element-2056" data-mp="1z13kbqna5ehz">
                      <span className="sr-only" data-id="element-2057" data-mp="11emlui2t8uun">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-2058" data-mp="16cxwgv0xj9dx">
                  {pageItems.map(pathway => <SavedPathwayRow key={pathway.id} pathway={pathway} expanded={expandedId === pathway.id} selected={selectedIds.has(pathway.id)} onToggleSelect={() => toggleSelection(pathway.id)} onToggleExpand={() => setExpandedId(expandedId === pathway.id ? null : pathway.id)} onOpen={onOpen} onDuplicate={onDuplicate} data-id="element-2059" data-mp="1bapvsa3wlxs5" />)}
                </tbody>
              </table>
            </div>
          </div>

          <PathwayPagination label="Your pathways pages" page={safePage} pageCount={pageCount} onChange={setPage} data-id="element-2060" data-mp="3h4iqw7adtrh5" />

          {selectedPathways.length > 0 && <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#30718D]/20 bg-[#30718D]/5 px-4 py-3" data-id="element-2061" data-mp="3r26y3wp6crg7">
              <p className="text-sm font-medium text-[#265a70]" data-id="element-2062" data-mp="4phu4kduhlzk">
                {selectedPathways.length}{' '}
                {selectedPathways.length === 1 ? 'pathway' : 'pathways'} selected
                for a combined export.
              </p>
              <div className="flex gap-3" data-id="element-2063" data-mp="dnacxyirzbrp">
                <button type="button" onClick={() => setSelectedIds(new Set())} className="text-sm font-medium text-[#265a70] underline" data-id="element-2064" data-mp="xan6yw0gsnx2">
                  Clear selection
                </button>
                <PathwayButton size="sm" onClick={() => onExport(selectedPathways)} data-id="element-2065" data-mp="sbtyji01bn3r">
                  <Download size={15} data-id="element-2066" data-mp="sdn5yzih2bct" />
                  Export / Share
                </PathwayButton>
              </div>
            </div>}
        </div>}
    </section>;
}